import React from "react"
import { Link } from "gatsby"
import { Helmet } from "react-helmet"
import { Collapse } from "antd"
import { CaretRightOutlined } from "@ant-design/icons"

import Layout from "../components/Layout"
import PricingGate, { PricingRecognised } from "../components/PricingGate"
import { usePricingUnlocked } from "../services/pricingAccess"
import { offers, isGated } from "../data/offers"
import { splitMetric } from "../utils/metric"

import "../styles/offerPage.scss"

const { Panel } = Collapse
const SITE = "https://www.visiomate.com"

/**
 * OFFER PAGE — the template for all nineteen.
 *
 * Structure is the five sections the brief specifies, in order:
 *
 *   1  Who this is for, and what it costs you now   Pattern A, sticky + rail
 *   2  How we solve it                              the vertical timeline
 *   3  What you get                                 in / not in, two columns
 *   4  Proof                                        stat construction
 *   5  Timeline, price and what we commit to        price at $fs-stat
 *
 * Then two tail sections and ONE closing CTA. The tail is not decoration:
 *
 *   FAQ       carries the FAQPage JSON-LD that OFFERS-BUILD-SPEC §6.1 requires
 *             on every offer page. Deleting the section deletes the schema.
 *   Related   carries the two sibling-offer links required by §7.3, which
 *             §7.5 then checks for orphans. Deleting it orphans pages.
 *
 * Both are flagged in the Gate 2 report. If they should go, the schema and the
 * link obligations need somewhere else to live first.
 *
 * ONE call to action, at the close. The previous template had three — hero,
 * price block and a CTA banner — all reading "Book a fit call", which is three
 * asks for one decision.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONSTRUCTION REGISTER (DESIGN-SYSTEM §3). No two adjacent sections share both
 * their ground and their padding:
 *
 *   1  who      Anchor      $paper   $s-12    $fs-2xl    41.6px
 *   2  how      Inverted    $deep    $s-12    $fs-2xl    41.6px
 *   3  get      Open        $white   $s-12    $fs-2xl    41.6px
 *   4  proof    Ruled       $paper   $s-8     $fs-stat     72px   + hairline both edges
 *   5  price    Open        $white   $s-12    $fs-stat     72px
 *   6  faq      Compressed  $paper   $s-8     $fs-xl       32px
 *   7  related  Open        $white   $s-12    $fs-xl       32px
 *   8  cta      Inverted    $deep    $s-8     $fs-2xl    41.6px
 * ─────────────────────────────────────────────────────────────────────────────
 */

const Tag = ({ children }) => <span className="op-tag">{children}</span>

/* Proof metrics are typeset by the shared splitMetric in utils/metric.js. The
   local copy this replaces matched only `40%`-style openings, so "24/7 voice
   agent creating leads directly in Odoo" rendered with no numeral despite
   opening with one. FIX 2. */

const minPrice = priceDisplay => {
  const match = String(priceDisplay).match(/\$([\d,]+)/)
  return match ? Number(match[1].replace(/,/g, "")) : null
}

const OfferTemplate = ({ pageContext }) => {
  const s = pageContext

  const related = (s.related || [])
    .map(slug => offers.find(o => o.slug === slug && o.live))
    .filter(Boolean)

  const url = `${SITE}/offers/${s.slug}`
  const price = minPrice(s.priceDisplay)
  const lines = s.oneLiners && s.oneLiners.length ? s.oneLiners : [s.oneLiner]

  /**
   * THE GATE NOW COVERS THE PRICE ITSELF.
   *
   * Only the three diagnostic offers publish a figure. On every other offer the
   * price, the payment schedule, the tier breakdown and the exclusions all sit
   * behind the form, and "Get the full scope and pricing" reveals the scope and
   * the price together.
   *
   * The structured data follows the page rather than the catalogue. Google
   * requires an Offer's price to be visible where it is declared, so a gated
   * offer emits Service with NO offers block at all — declaring a price the
   * page withholds is a rich-results eligibility risk, and it also invites a
   * SERP snippet that promises a figure the visitor will not find.
   * Entry offers still emit price and minPrice, because they still show them.
   */
  const gated = isGated(s)
  const { unlocked } = usePricingUnlocked()

  const showHow = s.process.length > 0
  const showProof = !!(s.proof && s.proof.client && (s.proof.metrics || []).length)
  // Split once: figures go in the stat grid, figureless metrics below it.
  const proofSplit = ((s.proof && s.proof.metrics) || []).map(m => ({
    raw: m,
    ...splitMetric(m),
  }))
  const proofStats = proofSplit.filter(m => m.value)
  const proofNotes = proofSplit.filter(m => !m.value)
  const showFaq = s.faqs.length > 0

  /**
   * THE CONSTRUCTION REGISTER, COMPUTED — not hard-coded per section.
   *
   * DESIGN-SYSTEM §3: no two adjacent sections may share both their ground and
   * their padding. That was assured while every page rendered all eight
   * sections, and it stopped being true the moment they did not: thirteen
   * offers have no delivery plan, fourteen have no proof and thirteen no FAQ,
   * so `scope` and `terms` — both $white at $s-12 — became neighbours. Measured
   * before this change: two violations on each of the thirteen, and one on the
   * Fit Sprint as soon as its Proof section went.
   *
   * So the grounds alternate across whichever light sections actually render.
   * The two dark sections are fixed and simply reset the alternation, since a
   * pale section either side of one is not adjacent to anything.
   *
   * Proof takes whichever ground the alternation gives it and keeps its
   * hairlines. The hairline is what the Ruled class is FOR — the design system
   * says so in as many words: "A rule is a line. It reads at a glance, at any
   * luminance... The ground swap is done as well, but for §6.1a's benefit
   * rather than this one." §6.1a is exactly what this alternation now serves.
   */
  const ground = (() => {
    const DARK = new Set(["how", "cta"])
    const out = {}
    let prev = null
    ;[
      ["who", true],
      ["how", showHow],
      ["scope", true],
      ["proof", showProof],
      ["terms", true],
      ["faq", showFaq],
      ["related", true],
      ["cta", true],
    ].forEach(([key, rendered]) => {
      if (!rendered) return
      if (DARK.has(key)) {
        out[key] = "dark"
        prev = "dark"
        return
      }
      out[key] = prev === "paper" ? "white" : "paper"
      prev = out[key]
    })
    return out
  })()

  const g = key => `op-ground--${ground[key]}`

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.name,
    description: s.oneLiner,
    provider: { "@type": "Organization", name: "Visiomate", url: SITE },
    areaServed: "Worldwide",
    ...(gated
      ? {}
      : {
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price: String(price),
            priceSpecification: {
              "@type": "PriceSpecification",
              priceCurrency: "USD",
              minPrice: String(price),
              valueAddedTaxIncluded: false,
            },
            url,
            availability: "https://schema.org/InStock",
          },
        }),
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: s.faqs.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "Offers", item: `${SITE}/offers` },
      { "@type": "ListItem", position: 3, name: s.name, item: url },
    ],
  }

  return (
    <Layout pageTitle={s.metaTitle} descrip={s.metaDescription}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        {s.faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <article className="op">
        {/* ── 1. WHO THIS IS FOR, AND WHAT IT COSTS YOU NOW ──────────────────
            Pattern A (DESIGN-SYSTEM §5): the h1 sits BESIDE the symptom list in
            a sticky 38% aside, not above it. The symptoms run the full measure
            of the right column with a monospace rail down their left edge.
           ──────────────────────────────────────────────────────────────────── */}
        <section className={`op-who ${g("who")}`}>
          <div className="op-who__inner">
            <div className="op-who__aside">
              <nav className="op-crumbs" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span aria-hidden="true">/</span>
                <Link to="/offers">Offers</Link>
              </nav>

              <Tag>Who this is for</Tag>
              <h1 className="op-who__heading">{s.h1}</h1>

              {lines.map((line, i) => (
                <p className="op-who__lede" key={i}>
                  {line}
                </p>
              ))}

              <p className="op-who__for">{s.whoFor}</p>
            </div>

            <div className="op-who__main">
              <span className="op-who__label">What it costs you now</span>
              <ul className="op-symptoms">
                {s.symptoms.map((item, i) => (
                  <li className="op-symptoms__row" key={item}>
                    <span className="op-symptoms__num" aria-hidden="true">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="op-symptoms__text">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── 2. HOW WE SOLVE IT ─────────────────────────────────────────────
            The most important section on the page, and a real timeline: one
            continuous spine drawn down the list, a $signal node per stage, and
            every stage ending in a named deliverable. Not a card list — cards
            would say these are eight separate things rather than one sequence.
            Rendered only where the week-by-week actually exists. Thirteen
            offers carry catalogue content but no delivery plan yet, and an
            empty spine under a heading reading "0 stages" is worse than no
            section — it advertises the gap instead of simply not claiming one.
           ──────────────────────────────────────────────────────────────────── */}
        {showHow && (
        <section className="op-how">
          <div className="op-how__inner">
            <Tag>How we solve it</Tag>
            <h2 className="op-how__heading">
              {s.process.length} stages, each ending in something you can sign off
            </h2>
            <p className="op-how__lede">
              You see working software at every stage, not a status report. The
              last one is go-live.
            </p>

            <ol className="op-steps">
              {s.process.map((p, i) => (
                <li className="op-steps__item" key={`${p.step}-${p.title}`}>
                  <span className="op-steps__node" aria-hidden="true" />
                  <span className="op-steps__week">{p.step}</span>
                  <h3 className="op-steps__title">{p.title}</h3>
                  <p className="op-steps__deliverable">{p.deliverable}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
        )}

        {/* ── 3. WHAT YOU GET ────────────────────────────────────────────────
            Two columns, no cards. Signal ticks on what is in, slate en-dashes
            on what is not (DESIGN-PATCH §8).
           ──────────────────────────────────────────────────────────────────── */}
        <section className={`op-scope ${g("scope")}`}>
          <div className="op-scope__inner">
            <Tag>What you get</Tag>
            <h2 className="op-scope__heading">
              Everything in scope at the price quoted, and everything that is not
            </h2>

            <div className="op-scope__cols">
              <div className="op-scope__col">
                <h3 className="op-scope__sub">Included</h3>
                <ul className="op-list op-list--in">
                  {s.included.map(item => (
                    <li key={item}>
                      <span className="op-list__mark" aria-hidden="true">
                        &#10003;
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* The column is dropped, not left empty, on an ungated offer
                  whose exclusions have not been written yet — an "Not included"
                  heading over nothing reads as "nothing is excluded", which is
                  a claim rather than an omission. */}
              {((gated && !unlocked) || s.notIncluded.length > 0) && (
              <div className="op-scope__col">
                <h3 className="op-scope__sub">Not included</h3>
                {/* Exclusions are part of the gated detail. The inclusions
                    beside them stay open, so the reader can still see what the
                    figure buys before deciding whether to ask for the rest. */}
                {gated && !unlocked ? (
                  /* Statement only, no second gate trigger. The ask sits in the
                     price section below, and two links to the same modal on one
                     page is two asks for one decision. */
                  <div className="op-scope__gated">
                    <p className="op-scope__gatedtext">
                      The exclusions appear here alongside the price, once you
                      have told us who you are.
                    </p>
                  </div>
                ) : (
                  <ul className="op-list op-list--out">
                    {s.notIncluded.map(item => (
                      <li key={item}>
                        <span className="op-list__mark" aria-hidden="true">
                          &ndash;
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              )}
            </div>
          </div>
        </section>

        {/* ── 4. PROOF ───────────────────────────────────────────────────────
            Ruled class: $paper with a drawn hairline at both edges, which is an
            edge rather than a difference in shade. Stat construction — a 2px
            $signal rule above each figure, hairlines between, and no card.
            The client name and every metric are attributed content and are
            reproduced verbatim.
           ──────────────────────────────────────────────────────────────────── */}
        {/* A named client AND at least one measurement, or no section.
            Metrics alone were enough once, and an offer whose only "metric" was
            a sentence about itself rendered a Proof heading over a signal rule
            and nothing else. As the other offers get their proof filled in,
            this guard is what stops a half-populated entry doing it again. */}
        {showProof && (
          <section className={`op-proof ${g("proof")}`}>
            <div className="op-proof__inner">
              <Tag>Proof</Tag>
              <h2 className="op-proof__heading">What changed for a business like yours</h2>
              {/* FIX 2b/2c — the attribution. The name alone read as a
                  subtitle under the heading; "Client" carries the grammar. It
                  links only where caseStudyPath is a route that resolves, and
                  never to a client's own website. */}
              <p className="op-proof__attrib">
                <span className="op-proof__attriblabel">Client</span>
                {s.proof.caseStudyPath ? (
                  <Link
                    to={s.proof.caseStudyPath}
                    className="op-proof__clientlink"
                  >
                    {s.proof.client}
                  </Link>
                ) : (
                  <span className="op-proof__client">{s.proof.client}</span>
                )}
              </p>

              {/* FIX 2a — every stat leads with its figure. A metric with no
                  figure at all does NOT get one invented; it drops out of the
                  grid and renders below it, so the row stays uniform. */}
              {proofStats.length > 0 && (
                <div className="op-proof__grid" data-count={proofStats.length}>
                  {proofStats.map(m => (
                    <div className="op-stat" key={m.raw}>
                      <p className="op-stat__value">{m.value}</p>
                      <p className="op-stat__label">{m.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {proofNotes.length > 0 && (
                <ul className="op-proof__notes">
                  {proofNotes.map(m => (
                    <li className="op-proof__note" key={m.raw}>
                      {m.label}
                    </li>
                  ))}
                </ul>
              )}

              {/* Only ever rendered when the path is a route that resolves. */}
              {s.proof.caseStudyPath && (
                <p className="op-proof__link">
                  <Link to={s.proof.caseStudyPath}>
                    Read how {s.proof.client} got there &rarr;
                  </Link>
                </p>
              )}
            </div>
          </section>
        )}

        {/* ── 5. TIMELINE, PRICE AND WHAT WE COMMIT TO ───────────────────────
            The price is the loudest typographic element on the page — it is the
            thing no competitor publishes (DESIGN-PATCH §2.7). Both sides of the
            deal sit beneath it, divided by a vertical hairline, with the
            clock-pause condition stated rather than buried.
           ──────────────────────────────────────────────────────────────────── */}
        <section className={`op-terms ${g("terms")}`}>
          <div className="op-terms__inner">
            <Tag>Timeline, price and terms</Tag>
            <h2 className="op-terms__heading">
              What it costs, when you pay, and what happens if a date slips
            </h2>

            <div className="op-terms__figures">
              <div className="op-terms__figure">
                <span className="op-terms__label">Price</span>
                {gated && !unlocked ? (
                  <>
                    {/* Locked. No figure, and no promise of a document either —
                        the price appears right here once they identify
                        themselves. */}
                    {/* Not "the price, the tiers and the payment schedule" —
                        eleven of the fifteen gated offers have no priceDetail
                        written yet, so only the figure would appear on them. */}
                    <p className="op-terms__detail">
                      The price appears here as soon as you tell us who you are.
                    </p>
                    <p className="op-terms__gate">
                      <PricingGate offer={s} />
                    </p>
                  </>
                ) : (
                  <>
                    <p className="op-terms__price">{s.priceDisplay}</p>
                    {s.priceDetail && (
                      <p className="op-terms__detail">{s.priceDetail}</p>
                    )}
                    {/* Only on a gated offer, and only once unlocked — the three
                        diagnostics have always shown their price and were never
                        asked for anything. */}
                    {gated && <PricingRecognised />}
                  </>
                )}
              </div>
              <div className="op-terms__figure">
                <span className="op-terms__label">Timeline</span>
                <p className="op-terms__timeline">{s.timeline}</p>
                <p className="op-terms__detail">Kickoff within one week of agreement.</p>
              </div>
            </div>

            {/* Both sides of the deal, or neither. These are commercial
                commitments, so an offer that has not had them written yet shows
                nothing rather than a heading with an empty list under it — and
                the clock-pause line below goes with them, because it is the
                condition attached to a commitment this offer has not made. */}
            {(s.commitments.length > 0 || s.weNeed.length > 0) && (
            <>
            <div className="op-terms__split">
              <div className="op-terms__col">
                <h3 className="op-terms__sub">What we commit to</h3>
                <ul className="op-list op-list--in">
                  {s.commitments.map(item => (
                    <li key={item}>
                      <span className="op-list__mark" aria-hidden="true">
                        &#10003;
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="op-terms__col">
                <h3 className="op-terms__sub">What we need from you</h3>
                <ul className="op-list op-list--need">
                  {s.weNeed.map(item => (
                    <li key={item}>
                      <span className="op-list__mark" aria-hidden="true">
                        &rarr;
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* The condition the fixed date depends on. Stated plainly, because
                a fixed timeline that quietly depends on the client is not a
                fixed timeline. */}
            <p className="op-terms__pause">
              The date holds while decisions come back inside 48 hours. If they
              stall, the clock pauses and the price does not move.
            </p>
            </>
            )}

            {/* The /pricing/ cross-link used to sit here, as a stray underlined
                line under the terms. It is onward navigation, not a term of the
                deal, so it has moved into Related below — where the other
                "somewhere else to go" links already live. */}
          </div>
        </section>

        {/* ── FAQ — carries the FAQPage schema (BUILD-SPEC §6.1) ───────────── */}
        {showFaq && (
          <section className={`op-faq ${g("faq")}`}>
            <div className="op-faq__inner">
              <Tag>Questions</Tag>
              <h2 className="op-faq__heading">
                Questions buyers ask before they commit
              </h2>
              <Collapse
                bordered={false}
                expandIcon={({ isActive }) => (
                  <CaretRightOutlined rotate={isActive ? 90 : 0} />
                )}
                className="op-faq__list"
              >
                {s.faqs.map((f, i) => (
                  <Panel header={f.q} key={i} className="op-faq__panel">
                    <p>{f.a}</p>
                  </Panel>
                ))}
              </Collapse>
            </div>
          </section>
        )}

        {/* ── Related — the sibling links §7.3 requires, plus the parent
            service page, which closes gap G6 in CONTENT-ARCHITECTURE §5. ──── */}
        {/* Always rendered: even an offer with no siblings and no parent service
            page carries the /pricing/ row, and every layer-2 page owes that link. */}
        <section className={`op-related ${g("related")}`}>
            <div className="op-related__inner">
              <Tag>Related</Tag>
              <h2 className="op-related__heading">If this is not quite it</h2>
              <ul className="op-related__list">
                {related.map(r => (
                  <li className="op-related__item" key={r.slug}>
                    <Link to={`/offers/${r.slug}`} className="op-related__link">
                      <span className="op-related__name">{r.name}</span>
                      <span className="op-related__meta">
                        {/* A gated sibling shows no figure here either. */}
                        {isGated(r)
                          ? r.timelineShort
                          : `${r.timelineShort} · ${r.priceShort}`}
                      </span>
                    </Link>
                  </li>
                ))}
                {s.servicePage && (
                  <li className="op-related__item">
                    <Link to={s.servicePage} className="op-related__link">
                      <span className="op-related__name">
                        Work that does not fit a fixed scope
                      </span>
                      <span className="op-related__meta">Scoped to your situation</span>
                    </Link>
                  </li>
                )}
                {/* ARCHITECTURE §5 has every layer-2 page linking to
                    /pricing/. That page is gone — the catalogue at /offers/
                    carries every offer and timeline, and the price now arrives
                    with the scope — so this row points there instead. It is the
                    obligation the §5 rule was for, met by the page that took
                    over the job. */}
                <li className="op-related__item">
                  <Link to="/offers" className="op-related__link">
                    <span className="op-related__name">
                      Every offer, side by side
                    </span>
                    <span className="op-related__meta">
                      The whole catalogue with timelines
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          </section>

        {/* ── The one call to action ─────────────────────────────────────── */}
        <section className="op-cta">
          <div className="op-cta__inner">
            <Tag>Next step</Tag>
            <h2 className="op-cta__heading">
              Tell us where your process actually breaks
            </h2>
            <p className="op-cta__lede">
              A fit call is a conversation about your operation, not a demo. If
              this is the wrong build for you, that is what you will hear.
            </p>
            <Link to="/contact" className="op-cta__btn">
              Book a fit call
            </Link>
          </div>
        </section>
      </article>
    </Layout>
  )
}

export default OfferTemplate
